export function isSameRowId(a, b) {
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

export function findParentRowIndex(tableData, parentId) {
    if (!Array.isArray(tableData) || parentId == null) return -1;

    return tableData.findIndex((row) => isSameRowId(row?.id, parentId) && !row?.isChildren);
}

export function getParentRow(tableData, parentId) {
    const index = findParentRowIndex(tableData, parentId);
    return index >= 0 ? tableData[index] : null;
}

export function getChildRows(tableData, parentId) {
    if (!Array.isArray(tableData)) return [];

    return tableData.filter((row) => row?.isChildren && isSameRowId(row?.parentId, parentId));
}

export function getParentRowIndexInGrid(api, parentId) {
    if (!api || parentId == null) return -1;

    let parentIndex = -1;
    api.forEachNode((node) => {
        const data = node.data;
        if (data && !data.isChildren && !data.isLoader && isSameRowId(data.id, parentId)) {
            parentIndex = node.rowIndex ?? -1;
        }
    });

    return parentIndex;
}

export function getParentDataFromGrid(api, parentId) {
    if (!api || parentId == null) return null;

    let parentData = null;
    api.forEachNode((node) => {
        const data = node.data;
        if (data && !data.isChildren && !data.isLoader && isSameRowId(data.id, parentId)) {
            parentData = data;
        }
    });

    return parentData;
}

export function getChildRowsFromGrid(api, parentId) {
    if (!api || parentId == null) return [];

    const children = [];
    api.forEachNode((node) => {
        const data = node.data;
        if (data?.isChildren && isSameRowId(data.parentId, parentId)) {
            children.push(data);
        }
    });

    return children;
}

export function isParentGroupOpen(api, parentId) {
    const parent = getParentDataFromGrid(api, parentId);
    return Boolean(parent?.isGroupOpen);
}

export function collectRowDataFromGrid(api) {
    const rows = [];
    api?.forEachNode?.((node) => {
        if (node?.data) rows.push(node.data);
    });
    return rows;
}

function getRowNodeForParent(api, parentId) {
    if (!api || parentId == null) return null;

    const rowId = String(parentId);
    const byId = api.getRowNode?.(rowId);
    if (byId?.data && !byId.data.isChildren && !byId.data.isLoader) return byId;

    let found = null;
    api.forEachNode((node) => {
        const data = node.data;
        if (data && !data.isChildren && !data.isLoader && isSameRowId(data.id, parentId)) {
            found = node;
        }
    });

    return found;
}

export function patchParentRowInGrid(api, parentId, patch) {
    const node = getRowNodeForParent(api, parentId);
    if (!node?.data) return null;

    const updated = { ...node.data, ...patch };
    Object.keys(patch).forEach((key) => {
        if (patch[key] === undefined) delete updated[key];
    });

    if (typeof node.setData === 'function') {
        node.setData(updated);
    } else {
        api.applyTransaction({ update: [updated] });
    }
    return updated;
}

export function refreshParentRow(api, parentId) {
    const node = getRowNodeForParent(api, parentId);
    if (!node) return;
    api.redrawRows?.({ rowNodes: [node] });
}

function getGridRootElement(context) {
    if (!context?.tableId || typeof document === 'undefined') return null;
    return document.getElementById(context.tableId);
}

export function runWithoutGroupRowAnimation(api, context, callback) {
    if (!api) return;

    if (typeof context === 'function') {
        callback = context;
        context = null;
    }

    if (typeof callback !== 'function') return;

    const wasAnimated = api.getGridOption('animateRows');
    const gridRoot = getGridRootElement(context);

    if (wasAnimated) api.setGridOption('animateRows', false);
    gridRoot?.classList.add('w5-grid-group-rows-static');

    callback();

    window.setTimeout(() => {
        gridRoot?.classList.remove('w5-grid-group-rows-static');
        if (wasAnimated && !api.isDestroyed?.()) api.setGridOption('animateRows', true);
    }, 0);
}

export function isParentGroupExpanded(api, parentId) {
    const parent = getParentDataFromGrid(api, parentId);
    if (parent?.isGroupOpen === true) return true;
    return getChildRowsFromGrid(api, parentId).length > 0;
}

export function syncTableDataFromGrid(api, context) {
    if (!api || !context?.updateTableDataRefs) return;
    context.updateTableDataRefs(collectRowDataFromGrid(api));
}

function setGridRowData(api, context, rows) {
    const nextRows = Array.isArray(rows) ? rows : [];
    context?.updateTableDataRefs?.(nextRows);
    api?.setGridOption?.('rowData', nextRows);
    context?.commitGridRowData?.(nextRows);
}

export function commitTableDataFromGrid(api, context) {
    if (!api) return;
    const rows = collectRowDataFromGrid(api);
    context?.updateTableDataRefs?.(rows);
    context?.commitGridRowData?.(rows);
}

export function patchParentRow(tableData, parentId, patch) {
    if (!Array.isArray(tableData)) return tableData;

    return tableData.map((row) => {
        if (!isSameRowId(row?.id, parentId) || row?.isChildren) return row;

        const next = { ...row, ...patch };
        Object.keys(patch).forEach((key) => {
            if (patch[key] === undefined) delete next[key];
        });
        return next;
    });
}

export function insertRowsAfterParent(tableData, parentId, rowsToInsert) {
    const parentIndex = findParentRowIndex(tableData, parentId);
    if (parentIndex < 0 || !rowsToInsert?.length) return tableData;

    return [
        ...tableData.slice(0, parentIndex + 1),
        ...rowsToInsert,
        ...tableData.slice(parentIndex + 1),
    ];
}

export function removeChildRows(tableData, parentId) {
    if (!Array.isArray(tableData)) return tableData;

    return tableData.filter((row) => !(row?.isChildren && isSameRowId(row?.parentId, parentId)));
}

export function removeLoadingRow(tableData, parentId) {
    if (!Array.isArray(tableData)) return tableData;

    const loadingRowId = `loadingRow${parentId}`;
    return tableData.filter((row) => !(row?.isLoader && isSameRowId(row?.id, loadingRowId)));
}

export function hasExpandedGroupRows(tableData) {
    if (!Array.isArray(tableData) || tableData.length === 0) return false;

    return tableData.some(
        (row) => row?.isChildren || row?.isLoader || (row?.isGroup && row?.isGroupOpen === true),
    );
}

export function collapseAllExpandedGroups(tableData) {
    if (!Array.isArray(tableData)) return [];

    const openParentIds = new Set(
        tableData.filter((row) => row?.isGroup && row?.isGroupOpen).map((row) => String(row.id)),
    );

    return tableData
        .filter(
            (row) =>
                !row?.isLoader && !(row?.isChildren && openParentIds.has(String(row?.parentId))),
        )
        .map((row) => {
            if (!row?.isGroup || !row?.isGroupOpen) return row;

            const next = { ...row };
            delete next.isGroupOpen;
            delete next.children;
            delete next.isLoading;
            return next;
        });
}

export function getParentRowIds(tableData) {
    if (!Array.isArray(tableData)) return '';

    return tableData
        .filter((row) => !row?.isChildren && !row?.isLoader)
        .map((row) => row.id)
        .join('|');
}

export function mergeParentRowsPreservingExpansions(incoming, current) {
    if (!Array.isArray(incoming) || !Array.isArray(current)) return incoming;

    const incomingParents = incoming.filter((row) => !row?.isChildren && !row?.isLoader);
    const incomingById = new Map(incomingParents.map((row) => [String(row.id), row]));
    const merged = [];

    current.forEach((row) => {
        if (row?.isChildren || row?.isLoader) {
            const parent = current.find(
                (candidate) =>
                    isSameRowId(candidate?.id, row?.parentId) &&
                    candidate?.isGroup &&
                    !candidate?.isChildren,
            );
            if (parent && incomingById.has(String(parent.id))) {
                merged.push(row);
            }
            return;
        }

        const updatedParent = incomingById.get(String(row.id));
        if (!updatedParent) return;

        const nextParent = { ...updatedParent };
        const hasExpandedChildren = current.some(
            (candidate) => candidate?.isChildren && isSameRowId(candidate.parentId, row.id),
        );
        if (row.isGroupOpen || hasExpandedChildren) {
            nextParent.isGroupOpen = row.isGroupOpen ?? hasExpandedChildren;
            nextParent.children = row.children;
            if (row.isLoading) nextParent.isLoading = row.isLoading;
        }
        merged.push(nextParent);
    });

    incomingParents.forEach((row) => {
        if (!merged.some((existing) => isSameRowId(existing.id, row.id) && !existing.isChildren)) {
            merged.push(row);
        }
    });

    return merged;
}

export function createGroupLoadingRow(parentId) {
    return {
        id: `loadingRow${parentId}`,
        isFullWidth: true,
        isLoader: true,
    };
}
